import React from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, MenuItem, Stack, Alert } from '@mui/material';
import { useTranslation } from 'react-i18next';
import useAxios from 'utils/useAxios';

const FEEDBACK_TYPES = [
    { value: 'data_issue', labelKey: 'feedback.type.data_issue' },
    { value: 'suggestion', labelKey: 'feedback.type.suggestion' },
    { value: 'anomaly', labelKey: 'feedback.type.anomaly' }
];

export default function FeedbackDialog({ open, onClose }) {
    const { t } = useTranslation();
    const axios = useAxios();
    const [type, setType] = React.useState('data_issue');
    const [message, setMessage] = React.useState('');
    const [submitting, setSubmitting] = React.useState(false);
    const [error, setError] = React.useState('');
    const [success, setSuccess] = React.useState(false);

    const pageUrl = typeof window !== 'undefined' ? window.location.pathname + window.location.search : '';

    React.useEffect(() => {
        if (open) {
            const setMenuZIndex = () => {
                const menuElements = document.querySelectorAll('.MuiModal-root.MuiPopover-root.MuiMenu-root');
                menuElements.forEach((el) => {
                    if (el instanceof HTMLElement) {
                        el.style.zIndex = '1000002';
                    }
                });
            };

            // Set immediately
            setMenuZIndex();

            // Watch for dynamically added menu elements
            const observer = new MutationObserver(() => {
                setMenuZIndex();
            });

            observer.observe(document.body, {
                childList: true,
                subtree: true
            });

            // Also set on intervals to catch any missed elements
            const intervalId = setInterval(setMenuZIndex, 100);

            return () => {
                observer.disconnect();
                clearInterval(intervalId);
            };
        }
    }, [open]);

    const handleSubmit = async () => {
        setSubmitting(true);
        setError('');
        setSuccess(false);
        try {
            await axios.post('feedback/', {
                type,
                message,
                page_url: pageUrl
            });
            setSuccess(true);
            setMessage('');
        } catch (e) {
            setError(t('feedback.submit_error'));
        } finally {
            setSubmitting(false);
        }
    };

    const handleClose = () => {
        setError('');
        setSuccess(false);
        onClose?.();
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth sx={{ zIndex: 1000001 }}>
            <DialogTitle>{t('feedback.title')}</DialogTitle>
            <DialogContent>
                <Stack spacing={2} sx={{ mt: 1 }}>
                    {error && <Alert severity="error">{error}</Alert>}
                    {success && <Alert severity="success">{t('feedback.success')}</Alert>}
                    <TextField
                        select
                        label={t('feedback.type.label')}
                        value={type}
                        onChange={(e) => setType(e.target.value)}
                        fullWidth
                        SelectProps={{
                            MenuProps: {
                                PaperProps: {
                                    sx: { zIndex: 1000002 }
                                },
                                anchorOrigin: {
                                    vertical: 'bottom',
                                    horizontal: 'left'
                                },
                                transformOrigin: {
                                    vertical: 'top',
                                    horizontal: 'left'
                                }
                            }
                        }}
                    >
                        {FEEDBACK_TYPES.map((opt) => (
                            <MenuItem key={opt.value} value={opt.value}>
                                {t(opt.labelKey) || opt.value}
                            </MenuItem>
                        ))}
                    </TextField>
                    <TextField
                        label={t('feedback.message')}
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        multiline
                        minRows={4}
                        fullWidth
                    />
                    <TextField label={t('feedback.page')} value={pageUrl} InputProps={{ readOnly: true }} fullWidth />
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} disabled={submitting}>
                    {t('common.close')}
                </Button>
                <Button onClick={handleSubmit} variant="contained" disabled={submitting || !message.trim()}>
                    {t('common.submit')}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
